import React, { useState, useEffect, useRef } from 'react';
import './BlueprintScrollViewer.css';
import { v4 as uuidv4 } from 'uuid'; // Import uuid for generating unique IDs in frontend

const BlueprintScrollViewer = ({ allPageImageUrls, roomData, onClose, projectId, blueprintId, analysisId, onSaveAnalysisChanges, onRoomDotClick }) => {
  const [imageDimensions, setImageDimensions] = useState({});
  const imgRefs = useRef({});
  const pageContainerRefs = useRef({});

  const [editableRoomData, setEditableRoomData] = useState(null); 

  const [editMode, setEditMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [draggingDot, setDraggingDot] = useState(null);
  const [addMode, setAddMode] = useState(false);

  // Effect to reset addMode when editMode changes
  useEffect(() => {
    if (!editMode) {
      setAddMode(false);
    }
  }, [editMode]);

  // Function to update dimensions for a specific image
  const updateImageDimensions = (imageUrl) => {
    const imgElement = imgRefs.current[imageUrl];
    if (imgElement && imgElement.naturalWidth && imgElement.naturalHeight) {
      setImageDimensions(prev => {
        const newDimensions = {
          width: imgElement.offsetWidth,
          height: imgElement.offsetHeight,
          naturalWidth: imgElement.naturalWidth,
          naturalHeight: imgElement.naturalHeight,
        };
        return {
          ...prev,
          [imageUrl]: newDimensions
        };
      });
    }

  };

  // Effect to capture initial image dimensions and re-capture on window resize
  useEffect(() => {
    allPageImageUrls.forEach(pageObj => {
      setTimeout(() => updateImageDimensions(pageObj.url), 100);
    });

    const handleResize = () => {
      allPageImageUrls.forEach(pageObj => {
        updateImageDimensions(pageObj.url);
      });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('resize', handleResize); // Ensure resize listener is cleaned up
    };
  }, [allPageImageUrls]); // Depend on allPageImageUrls to re-run if blueprint changes

  // Scroll to first page with rooms/features when data first loads
  useEffect(() => {
    if (!roomData || roomData.length === 0 || allPageImageUrls.length === 0) return;
    const firstPageWithRooms = allPageImageUrls.find((pageObj, idx) => {
      const rd = roomData.find(r => r.pageIndex === idx || r.url === pageObj.url || r.imageUrl === pageObj.url);
      return rd && rd.floors && rd.floors.some(f => f.rooms && f.rooms.length > 0);
    });
    if (firstPageWithRooms) {
      const el = pageContainerRefs.current[firstPageWithRooms.url];
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
      }
    }
  }, [roomData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImageLoad = (e, imageUrl) => {
    // console.log(`DEBUG_VIEWER: Image loaded: ${imageUrl.substring(imageUrl.lastIndexOf('/') + 1)}`);
    updateImageDimensions(imageUrl);
  };

  // --- Dragging Logic ---
  const handleMouseDown = (e, pageId, floorId, roomId) => {
    if (!editMode || addMode || editableRoomData === null) return; 
    if (e.target.classList.contains('delete-dot-btn')) {
      return;
    }

    e.preventDefault();

    const dotElement = e.currentTarget;
    const imgElement = imgRefs.current[pageId];
    if (!imgElement) return;

    const imgRect = imgElement.getBoundingClientRect();
    
    const currentLeft = dotElement.offsetLeft;
    const currentTop = dotElement.offsetTop;

    setDraggingDot({
      pageId,
      floorId,
      roomId,
      startX: e.clientX,
      startY: e.clientY,
      originalLeft: currentLeft,
      originalTop: currentTop,
      imgRect: imgRect,
    });
  };

  const handleMouseMove = (e) => {
    if (!draggingDot) return;
    e.preventDefault();

    const { pageId, floorId, roomId, startX, startY, originalLeft, originalTop, imgRect } = draggingDot;
    const dimensions = imageDimensions[pageId];
    
    // Retrieve original dimensions from the allPageImageUrls prop for the current page
    // Find the pageObj that matches the current pageId (which is the imageUrl)
    const originalPageData = allPageImageUrls.find(page => page.url === pageId);

    // Ensure we have both displayed dimensions and original dimensions from Firestore
    if (!dimensions || !originalPageData || !originalPageData.width || !originalPageData.height) return;

    const dx = e.clientX - startX;
    const dy = e.clientY - startY;

    let newLeftPx = originalLeft + dx;
    let newTopPx = originalTop + dy;

    const dotHalfWidth = 10; // Assuming dot is 20x20, so half is 10
    const dotHalfHeight = 10;

    // Constrain dot position within the image boundaries (accounting for dot size)
    newLeftPx = Math.max(dotHalfWidth, Math.min(newLeftPx, imgRect.width - dotHalfWidth));
    newTopPx = Math.max(dotHalfHeight, Math.min(newTopPx, imgRect.height - dotHalfHeight));

    setEditableRoomData(prevData => {
      if (prevData === null) return null; 
      
      const newData = prevData.map(page => {
        if (page.imageUrl === pageId) { // page.imageUrl here is the Firebase Storage URL of the page
          return {
            ...page,
            floors: page.floors.map(floor => {
              if (floor.id === floorId) {
                return {
                  ...floor,
                  rooms: floor.rooms.map(room => {
                    if (room.id === roomId) {
                      // Calculate scale based on *displayed* image size and *original* image size
                      const scaleX = dimensions.width / originalPageData.width;
                      const scaleY = dimensions.height / originalPageData.height;
                      
                      // Convert new pixel position back to original image coordinates
                      const newX = Math.round(newLeftPx / scaleX);
                      const newY = Math.round(newTopPx / scaleY);
                      // console.log(`DEBUG_VIEWER: Moving room ${room.name} on page ${pageId.substring(pageId.lastIndexOf('/') + 1)} to X:${newX}, Y:${newY}`);
                      return {
                        ...room,
                        x: newX,
                        y: newY
                      };
                    }
                    return room;
                  })
                };
              }
              return floor;
            })
          };
        }
        return page;
      });

      setHasUnsavedChanges(true);
      return newData;
    });
  };

  const handleMouseUp = () => {
    setDraggingDot(null);
  };

  useEffect(() => {
    if (draggingDot) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    // Cleanup function for useEffect
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingDot]); // Re-run effect when draggingDot changes

  // --- Save Changes Logic ---
  const handleSave = async () => {
    if (onSaveAnalysisChanges && editableRoomData !== null) {
      await onSaveAnalysisChanges(projectId, blueprintId, analysisId, editableRoomData);
      setHasUnsavedChanges(false);
    }
  };

  // --- Delete Room Logic ---
  const handleDeleteDot = (e, pageId, floorId, roomId) => {
    e.stopPropagation(); // Prevent click from bubbling to image (if in add mode)
    if (!editMode || editableRoomData === null) return;

    const roomToDelete = editableRoomData.find(p => p.imageUrl === pageId)
                                        ?.floors.find(f => f.id === floorId)
                                        ?.rooms.find(r => r.id === roomId);

    if (window.confirm(`Are you sure you want to delete the room dot for "${roomToDelete?.name || 'this room'}"?`)) {
        setEditableRoomData(prevData => {
            if (prevData === null) return null;
            
            const newData = prevData.map(page => {
                if (page.imageUrl === pageId) {
                    const newFloors = page.floors.map(floor => {
                        if (floor.id === floorId) {
                            return {
                                ...floor,
                                rooms: floor.rooms.filter(room => room.id !== roomId)
                            };
                        }
                        return floor;
                    }).filter(floor => floor.rooms.length > 0); // Remove floor if no rooms left
                    return {
                        ...page,
                        floors: newFloors
                    };
                }
                return page;
            }).filter(page => page.floors.length > 0); // Remove page if no floors left

            setHasUnsavedChanges(true);
            return newData;
        });
    }
  };

  // --- Add Room Logic ---
  const handleImageClick = async (e, pageId) => {
    if (!editMode || !addMode || draggingDot || editableRoomData === null) return;

    const imgElement = imgRefs.current[pageId];
    if (!imgElement) return;

    const imgRect = imgElement.getBoundingClientRect();
    const clickX = e.clientX - imgRect.left; // X relative to image
    const clickY = e.clientY - imgRect.top; // Y relative to image

    const dimensions = imageDimensions[pageId];
    const originalPageData = allPageImageUrls.find(page => page.url === pageId);

    if (!dimensions || !originalPageData || !originalPageData.width || !originalPageData.height) return;

    // Calculate position on the original image's pixel scale
    const originalX = Math.round(clickX / (dimensions.width / originalPageData.width));
    const originalY = Math.round(clickY / (dimensions.height / originalPageData.height));

    const roomName = window.prompt("Enter room name for the new dot:");
    if (!roomName || roomName.trim() === "") {
      return;
    }

    // Ask for floor name, default to "Unknown"
    const floorName = window.prompt("Enter floor name for the new dot (e.g., '1st Floor', 'Basement', or leave blank for 'Unknown'):") || "Unknown";


    const newRoom = {
      id: uuidv4(), // Generate a unique ID for the new room
      name: roomName.trim(),
      x: originalX,
      y: originalY,
    };


    setEditableRoomData(prevData => {
      // Ensure prevData is an array, even if null initially
      let currentPrevData = Array.isArray(prevData) ? prevData : [];

      // Find the page in prevData that matches the current pageId
      let pageToUpdate = currentPrevData.find(page => page.imageUrl === pageId);
      
      // If the page doesn't exist in editableRoomData yet, create a new entry for it
      if (!pageToUpdate) {
        pageToUpdate = { imageUrl: pageId, floors: [], width: originalPageData.width, height: originalPageData.height, pageIndex: originalPageData.pageIndex }; // Include dimensions and pageIndex
        currentPrevData = [...currentPrevData, pageToUpdate]; // Add it to currentPrevData for the next steps
      }

      const newData = currentPrevData.map(page => {
        if (page.imageUrl === pageId) {
          // Try to find the specified floor or create one
          let targetFloor = page.floors.find(f => f.floor === floorName);
          let newFloors;

          if (!targetFloor) {
            // If no matching floor, add a new floor with the new room
            newFloors = [...page.floors, { id: uuidv4(), floor: floorName, rooms: [newRoom] }];
          } else {
            // If matching floor exists, update its rooms
            newFloors = page.floors.map(floor => {
              if (floor.id === targetFloor.id) { // Use floor.id for matching
                return {
                  ...floor,
                  rooms: [...floor.rooms, newRoom]
                };
              }
              return floor;
            });
          }

          return {
            ...page,
            floors: newFloors
          };
        }
        return page;
      });

      setHasUnsavedChanges(true);
      return newData;
    });
    setAddMode(false); // Exit add mode after adding a dot
  };

  // --- Handle Edit Mode Toggle ---
  const handleToggleEditMode = async () => {
    if (editMode) {
        if (hasUnsavedChanges) {
            const confirmSave = window.confirm("You have unsaved changes. Do you want to save them now?");
            if (confirmSave) {
                await handleSave(); 
            } else {
                setHasUnsavedChanges(false); 
            }
        }
        setEditMode(false);
        setAddMode(false);
        setEditableRoomData(null); // Discard editable changes
        //console.log("DEBUG_VIEWER: Exited edit mode. Discarded changes.");
    } else {
        // Deep copy roomData to editableRoomData
        const freshEditableData = roomData.map(page => ({
            ...page,
            imageUrl: page.url, // Ensure imageUrl is set from page.url for consistency
            floors: page.floors.map(floor => ({
                id: floor.id || uuidv4(), // Ensure floor has an ID
                ...floor,
                rooms: floor.rooms.map(room => ({
                    id: room.id || uuidv4(), // Ensure room has an ID
                    ...room
                }))
            }))
        }));
        setEditableRoomData(freshEditableData);
        setHasUnsavedChanges(false);
        setEditMode(true);
        //console.log("DEBUG_VIEWER: Entered edit mode. Initialized editableRoomData.");
    }
  };

  // --- Conditional Rendering of Room Data ---
  // Use editableRoomData if in editMode, otherwise use the original roomData prop
  const displayRoomData = editMode ? editableRoomData : roomData;

  // TEMP DEBUG — remove once dots confirmed working
  const pagesWithRooms = (displayRoomData || []).filter(rd => rd.floors && rd.floors.some(f => f.rooms && f.rooms.length > 0));
  const totalRooms = pagesWithRooms.reduce((acc, rd) => acc + rd.floors.reduce((a, f) => a + (f.rooms?.length || 0), 0), 0);
  const urlSample = pagesWithRooms[0] ? `rd.url=${pagesWithRooms[0].url?.substring(0, 60)}` : 'no rd with rooms';
  const imgSample = allPageImageUrls[0] ? `img.url=${allPageImageUrls[0].url?.substring(0, 60)}` : 'no imgs';
  console.log(`[VIEWER] pages=${allPageImageUrls.length} roomDataEntries=${(displayRoomData||[]).length} pagesWithRooms=${pagesWithRooms.length} totalRooms=${totalRooms}`);
  console.log(`[VIEWER] ${urlSample}`);
  console.log(`[VIEWER] ${imgSample}`);


  // Show loading state if no displayRoomData or allPageImageUrls are available
  // This check should be more robust, as allPageImageUrls will now be objects
  if (!displayRoomData || displayRoomData.length === 0 || allPageImageUrls.length === 0) {
    return (
      <div className="scroll-viewer-overlay">
        <div className="scroll-viewer-header">
          <button onClick={onClose} className="close-btn">✕</button>
          <h3>Analyzed Blueprint</h3>
          <div className="header-controls">
            <button onClick={handleToggleEditMode} className={`edit-mode-btn ${editMode ? 'active' : ''}`}>
              {editMode ? 'Exit Edit Mode' : 'Enter Edit Mode'}
            </button>
          </div>
        </div>
        <div className="loading-or-empty">Loading blueprint data...</div>
      </div>
    );
  }

  return (
    <div className="scroll-viewer-overlay">
      <div className="scroll-viewer-header">
        <button onClick={onClose} className="close-btn">✕</button>
        <h3>Analyzed Blueprint <span style={{fontSize:'0.7rem',fontWeight:'normal',color:'#aaa'}}>[{pagesWithRooms.length}pg/{totalRooms}rm]</span></h3>
        <div className="header-controls">
          <button onClick={handleToggleEditMode} className={`edit-mode-btn ${editMode ? 'active' : ''}`}>
            {editMode ? 'Exit Edit Mode' : 'Enter Edit Mode'}
          </button>
          {editMode && (
            <>
              <button
                onClick={() => setAddMode(!addMode)}
                className={`add-dot-btn ${addMode ? 'active' : ''}`}
                title="Click on the blueprint to add a new room dot"
              >
                {addMode ? 'Cancel Add' : 'Add Dot'}
              </button>
              <button onClick={handleSave} className="save-changes-btn" disabled={!hasUnsavedChanges}>
                Save Changes {hasUnsavedChanges && "*"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="scroll-viewer-pages">
        {allPageImageUrls.map((pageObj, index) => { // Iterate over page objects {url, width, height}
          const imageUrl = pageObj.url; // Extract the URL
          const pageId = imageUrl; // The unique ID for the page is its URL
          const dimensions = imageDimensions[pageId]; // Get displayed dimensions
          
          // Match by pageIndex first (reliable), fall back to URL matching for edit-mode entries
          const currentPageRoomData = displayRoomData.find(rd =>
            rd.pageIndex === index ||
            rd.url === imageUrl ||
            rd.imageUrl === imageUrl
          );

          //console.log(`DEBUG_VIEWER: Rendering page ${index} (${imageUrl.substring(imageUrl.lastIndexOf('/') + 1)}). Has dimensions: ${!!dimensions}. Has room data: ${!!currentPageRoomData}.`);

          let scaleX = 1;
          let scaleY = 1;
          if (dimensions && pageObj.width && pageObj.height) {
            scaleX = dimensions.width / pageObj.width;
            scaleY = dimensions.height / pageObj.height;
          } else if (pageObj.width && pageObj.height) {
            // Image not measured yet — estimate scale from the img element directly if available
            const imgEl = imgRefs.current[imageUrl];
            if (imgEl && imgEl.offsetWidth) {
              scaleX = imgEl.offsetWidth / pageObj.width;
              scaleY = imgEl.offsetHeight / pageObj.height;
            }
          }

          return (
            <div className="page-container" key={pageObj.url} ref={el => pageContainerRefs.current[imageUrl] = el}>
              <div className="page-image-wrapper">
                <img
                  src={imageUrl}
                  alt={`Page ${index + 1}`}
                  className="page-image"
                  ref={el => imgRefs.current[imageUrl] = el}
                  onLoad={(e) => handleImageLoad(e, pageId)}
                  onClick={editMode && addMode && !draggingDot ? (e) => handleImageClick(e, pageId) : undefined}
                  style={{ cursor: editMode && addMode && !draggingDot ? 'crosshair' : (editMode ? 'grab' : 'default') }}
                />

                {currentPageRoomData && currentPageRoomData.floors && currentPageRoomData.floors.map((floor) => {
                  return (
                  <React.Fragment key={floor.id}>
                    {floor.rooms && floor.rooms.map((room) => {
                      const dotLeft = room.x * scaleX;
                      const dotTop = room.y * scaleY;
                      const isExterior = currentPageRoomData?.pageType === 'exterior' || currentPageRoomData?.pageType === 'both';
                      const isDragging = draggingDot && draggingDot.pageId === pageId && draggingDot.floorId === floor.id && draggingDot.roomId === room.id;

                      const commonEventProps = {
                        title: room.name,
                        onMouseDown: editMode && !addMode ? (e) => handleMouseDown(e, pageId, floor.id, room.id) : undefined,
                        onClick: (e) => {
                          if (!editMode && !addMode) {
                            onRoomDotClick(room, floor.id, floor.floor);
                          } else if (addMode) {
                            e.stopPropagation();
                          }
                        },
                      };

                      if (isExterior) {
                        const centerX = (pageObj.width / 2) * scaleX;
                        const centerY = (pageObj.height / 2) * scaleY;
                        const angle = Math.atan2(centerY - dotTop, centerX - dotLeft);
                        const CONE_LENGTH = 52;
                        const CONE_HALF_ANGLE = Math.PI / 5;
                        const x1 = CONE_LENGTH * Math.cos(angle + CONE_HALF_ANGLE);
                        const y1 = CONE_LENGTH * Math.sin(angle + CONE_HALF_ANGLE);
                        const x2 = CONE_LENGTH * Math.cos(angle - CONE_HALF_ANGLE);
                        const y2 = CONE_LENGTH * Math.sin(angle - CONE_HALF_ANGLE);
                        const fillColor = editMode ? 'rgba(0,123,255,0.45)' : 'rgba(255,140,0,0.55)';
                        const strokeColor = editMode ? 'rgba(0,80,200,0.9)' : 'rgba(255,100,0,0.95)';

                        return (
                          <div
                            key={room.id}
                            {...commonEventProps}
                            style={{
                              position: 'absolute',
                              left: `${dotLeft}px`,
                              top: `${dotTop}px`,
                              width: 0,
                              height: 0,
                              cursor: editMode ? (addMode ? 'crosshair' : isDragging ? 'grabbing' : 'grab') : 'pointer',
                              zIndex: 10,
                            }}
                          >
                            <svg style={{ overflow: 'visible', position: 'absolute', top: 0, left: 0 }} width="0" height="0">
                              <path
                                d={`M 0 0 L ${x1.toFixed(2)} ${y1.toFixed(2)} L ${x2.toFixed(2)} ${y2.toFixed(2)} Z`}
                                fill={fillColor}
                                stroke={strokeColor}
                                strokeWidth="1.5"
                                strokeLinejoin="round"
                              />
                              <circle cx="0" cy="0" r="4" fill={strokeColor} />
                            </svg>
                            {editMode && <span className="room-dot-label" style={{ position: 'absolute', whiteSpace: 'nowrap' }}>{room.name}</span>}
                            {editMode && (
                              <button
                                className="delete-dot-btn"
                                onClick={(e) => handleDeleteDot(e, pageId, floor.id, room.id)}
                                title="Delete Room"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        );
                      }

                      return (
                        <div
                          key={room.id}
                          {...commonEventProps}
                          className={`room-dot ${editMode ? 'editable' : ''} ${isDragging ? 'dragging' : ''}`}
                          style={{
                            left: `${dotLeft}px`,
                            top: `${dotTop}px`,
                            cursor: editMode ? (addMode ? 'crosshair' : 'grab') : 'pointer',
                          }}
                        >
                          {editMode && <span className="room-dot-label">{room.name}</span>}
                          {editMode && (
                            <button
                              className="delete-dot-btn"
                              onClick={(e) => handleDeleteDot(e, pageId, floor.id, room.id)}
                              title="Delete Room"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                );})}
              </div>
              <p>Page {index + 1}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BlueprintScrollViewer;
